"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import { useDecisionPoints } from "@/hooks/waves/useDecisionPoints";
import { AnimatePresence } from "framer-motion";
import { TimelineToggleHeader } from "./time/TimelineToggleHeader";
import { ExpandedTimelineContent } from "./time/ExpandedTimelineContent";
import { CompactDroppingPhaseCard } from "./time/CompactDroppingPhaseCard";
import { CompactVotingPhaseCard } from "./time/CompactVotingPhaseCard";
import { useWave } from "@/hooks/useWave";
import { Time } from "@/helpers/time";
interface WaveLeaderboardTimeProps {
  readonly wave: ApiWave;
}
const AUTO_EXPAND_LIMIT = 5;
export const WaveLeaderboardTime: React.FC<WaveLeaderboardTimeProps> = t0 => {
  const $ = _c(22);
  const {
    wave
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      initialPastWindow: 4,
      initialFutureWindow: 20
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    allDecisions,
    hasMorePast,
    hasMoreFuture,
    loadMorePast,
    loadMoreFuture,
    remainingPastCount,
    remainingFutureCount
  } = useDecisionPoints(wave, t1);
  const {
    decisions: t2,
    pauses: t3
  } = useWave(wave);
  const {
    multiDecision
  } = t2;
  const {
    showPause,
    filterDecisionsDuringPauses
  } = t3;
  const [isDecisionDetailsOpen, setIsDecisionDetailsOpen] = useState(false);
  const [autoExpandFutureAttempts, setAutoExpandFutureAttempts] = useState(0);
  const [timelineFocus, setTimelineFocus] = useState(null);
  let t4;
  let t5;
  if ($[1] !== isDecisionDetailsOpen || $[2] !== timelineFocus) {
    t4 = () => {
      if (!isDecisionDetailsOpen && timelineFocus !== null) {
        setTimelineFocus(null);
      }
    };
    t5 = [isDecisionDetailsOpen, timelineFocus];
    $[1] = isDecisionDetailsOpen;
    $[2] = timelineFocus;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  if ($[5] !== allDecisions || $[6] !== filterDecisionsDuringPauses) {
    const decisionsAsApiFormat = allDecisions.map(_temp);
    const filtered = filterDecisionsDuringPauses(decisionsAsApiFormat);
    t6 = new Set(filtered.map(_temp2));
    $[5] = allDecisions;
    $[6] = filterDecisionsDuringPauses;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const allowedDecisionTimes = t6;
  let t7;
  if ($[8] !== allDecisions || $[9] !== allowedDecisionTimes) {
    let t8;
    if ($[11] !== allowedDecisionTimes) {
      t8 = decision_0 => allowedDecisionTimes.has(decision_0.timestamp);
      $[11] = allowedDecisionTimes;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    t7 = allDecisions.filter(t8);
    $[8] = allDecisions;
    $[9] = allowedDecisionTimes;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const filteredDecisions = t7;
  const nextDecisionTime = filteredDecisions.find(_temp3)?.timestamp ?? null;
  useEffect(() => {
    if (nextDecisionTime !== null) {
      if (autoExpandFutureAttempts !== 0) {
        setAutoExpandFutureAttempts(0);
      }
      return;
    }
    if (!hasMoreFuture) {
      if (autoExpandFutureAttempts !== 0) {
        setAutoExpandFutureAttempts(0);
      }
      return;
    }
    if (autoExpandFutureAttempts >= AUTO_EXPAND_LIMIT) {
      return;
    }
    const timeoutId = globalThis.setTimeout(() => {
      setAutoExpandFutureAttempts(_temp4);
      loadMoreFuture();
    }, 50);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [nextDecisionTime, hasMoreFuture, loadMoreFuture, autoExpandFutureAttempts]);
  let t8;
  if ($[13] !== hasMorePast || $[14] !== loadMorePast) {
    t8 = () => {
      if (hasMorePast) {
        setTimelineFocus("start");
        loadMorePast();
      }
    };
    $[13] = hasMorePast;
    $[14] = loadMorePast;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleLoadMorePast = t8;
  let t9;
  if ($[16] !== hasMoreFuture || $[17] !== loadMoreFuture) {
    t9 = () => {
      if (hasMoreFuture) {
        setTimelineFocus("end");
        loadMoreFuture();
      }
    };
    $[16] = hasMoreFuture;
    $[17] = loadMoreFuture;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleLoadMoreFuture = t9;
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setTimelineFocus(null);
    };
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const onTimelineFocusHandled = t10;
  const t11 = multiDecision ? <div className="tw-rounded-lg tw-bg-iron-950 tw-overflow-hidden">{(() => {
      const currentPause = showPause(nextDecisionTime);
      return <TimelineToggleHeader isOpen={isDecisionDetailsOpen} setIsOpen={setIsDecisionDetailsOpen} nextDecisionTime={nextDecisionTime} isPaused={Boolean(currentPause)} currentPause={currentPause} wave={wave} />;
    })()}<AnimatePresence>{isDecisionDetailsOpen && <ExpandedTimelineContent decisions={filteredDecisions} nextDecisionTime={nextDecisionTime} onLoadMorePast={hasMorePast ? handleLoadMorePast : undefined} onLoadMoreFuture={hasMoreFuture ? handleLoadMoreFuture : undefined} hasMorePast={hasMorePast} hasMoreFuture={hasMoreFuture} remainingPastCount={remainingPastCount} remainingFutureCount={remainingFutureCount} focus={timelineFocus} onFocusHandled={onTimelineFocusHandled} />}</AnimatePresence></div> : <div className="tw-rounded-lg tw-bg-iron-950 tw-px-3 tw-py-2 tw-overflow-hidden"><div className="tw-flex tw-items-center tw-gap-2"><CompactDroppingPhaseCard wave={wave} /><CompactVotingPhaseCard wave={wave} /></div></div>;
  let t12;
  if ($[20] !== t11) {
    t12 = <div>{t11}</div>;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  return t12;
};
function _temp(decision) {
  return {
    decision_time: decision.timestamp
  };
}
function _temp2(filteredDecision) {
  return filteredDecision.decision_time;
}
function _temp3(decision_1) {
  return decision_1.timestamp > Time.currentMillis();
}
function _temp4(prev) {
  return prev + 1;
}